"use client";
import { Input, useDisclosure, useToast } from "@chakra-ui/react";
import { useContext, useEffect, useState } from "react";
import { GetMarriageInfo } from "@/actions/templates/MarriageInfo/GetMarriageInfo";
import moment from "moment";
import HistorySection from "@/components/dashboard/history/HistorySection";
import HistoryRecordsTable from "@/components/dashboard/HistoryRecordsTable";
import {
  EVENT_LIBRARY,
  HISTORY_TEMPLATES,
  REQUIRED_FIELD,
} from "@/lib/constants";
import { DeleteMarriageInfo } from "@/actions/templates/MarriageInfo/DeleteMarriageInfo";
import {
  MarriageInfoPayload,
  PostMarriageInfo,
} from "@/actions/templates/MarriageInfo/PostMarriageInfo";
import { Controller, useForm } from "react-hook-form";
import HistoryModal from "@/components/dashboard/history/HistoryModal";
import FormRow from "@/components/dashboard/application/FormRow";
import LocationSelect from "@/components/LocationSelect";
import { PutMarriageInfo } from "@/actions/templates/MarriageInfo/PutMarriageInfo";
import { AuthContext } from "@/providers/auth";
import useAnalytics from "@/lib/hooks/useAnalytics";
import handleServerAction from "@/lib/hooks/handleServerAction";
import DateOfBirthInput from "@/components/forms/DateOfBirthInput";
import { useIsTextMasked } from "../children/useIsDataMasked";
import { InputWrapper } from "@/components/forms/JailTextInputWrapper";

const category = "Marriage";
const defaultValues = HISTORY_TEMPLATES[category].defaultValues;

const MarriageHistoryPageContent = ({
  application_template,
}: {
  application_template?: "current_marriage" | "prior_marriages";
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(true);
  const [records, setRecords] = useState<any[]>([]);
  const [isDivorced, setIsDivorced] = useState(false);

  const { getUserDetails } = useContext(AuthContext);
  const toast = useToast();
  const track = useAnalytics();

  const {
    handleSubmit,
    register,
    reset,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<MarriageInfoPayload>({
    defaultValues: defaultValues,
  });

  const refreshState = async () => {
    onClose();
    await getRecords();
    await getUserDetails();
    reset(defaultValues);
  };

  const getRecords = async () => {
    setIsLoading(true);
    const data = await handleServerAction(GetMarriageInfo);
    setRecords(
      data.filter((record: any) => {
        if (application_template === "current_marriage") {
          return record.dateOfDivorce === null;
        } else if (application_template === "prior_marriages") {
          return record.dateOfDivorce !== null;
        }
        return true;
      })
    );
    setIsLoading(false);
  };

  useEffect(() => {
    getRecords();
  }, []);

  const onEdit = (id: number) => {
    const editRecord = records.find((record) => record.id === id);
    if (editRecord) {
      reset({
        Id: editRecord?.id,
        DateOfMarriage: moment(editRecord?.dateOfMarriage).format("YYYY-MM-DD"),
        CityOfMarriage: editRecord?.cityOfMarriage,
        StateOfMarriage: editRecord?.stateOfMarriage,
        DateOfDivorce: editRecord?.dateOfDivorce
          ? moment(editRecord?.dateOfDivorce).format("YYYY-MM-DD")
          : undefined,
        CityOfDivorce: editRecord?.cityOfDivorce,
        StateOfDivorce: editRecord?.stateOfDivorce,
        SpouseName: editRecord?.spouseName,
        SpouseDob: moment(editRecord?.spouseDob).format("YYYY-MM-DD"),
        SpouseSSN: editRecord?.spouseSSN,
      });
      onOpen();
    }
  };

  const onAdd = async (payload: MarriageInfoPayload) => {
    try {
      await handleServerAction(PostMarriageInfo, {
        ...payload,
        DateOfMarriage: moment(payload.DateOfMarriage).format("YYYY-MM-DD"),
        DateOfDivorce: payload.DateOfDivorce
          ? moment(payload.DateOfDivorce).format("YYYY-MM-DD")
          : undefined,
        SpouseDob: moment(payload.SpouseDob).format("YYYY-MM-DD"),
      });
      await refreshState();
      toast({
        status: "success",
        title: "Record Added",
        description: "Record has been successfully added.",
      });

      track(EVENT_LIBRARY.ADDED_HISTORY, {
        category,
      });
    } catch (err) {
      toast({
        status: "error",
        title: "Error Occured",
        description: err.message,
      });
    }
  };

  const onUpdate = async (payload: MarriageInfoPayload) => {
    try {
      await handleServerAction(PutMarriageInfo, {
        ...payload,
        DateOfMarriage: moment(payload.DateOfMarriage).format("YYYY-MM-DD"),
        DateOfDivorce: payload.DateOfDivorce
          ? moment(payload.DateOfDivorce).format("YYYY-MM-DD")
          : undefined,
        SpouseDob: moment(payload.SpouseDob).format("YYYY-MM-DD"),
      });

      await refreshState();

      toast({
        status: "success",
        title: "Record Updated",
        description: "Record has been successfully updated.",
      });

      track(EVENT_LIBRARY.UPDATED_HISTORY, {
        category,
      });
    } catch (err) {
      toast({
        status: "error",
        title: "Error Occured",
        description: err.message,
      });
    }
  };

  const onClick = () => {
    reset(defaultValues);
    onOpen();
  };

  const Id = watch("Id");
  const DateOfDivorce = watch("DateOfDivorce");
  const isTextMasked = useIsTextMasked() && Id;

  return (
    <HistorySection category={category} isLoading={isLoading} onClick={onClick}>
      <HistoryRecordsTable
        category={category}
        records={records}
        cells={records.map((record) => [
          record.spouseName,
          moment(record.dateOfMarriage).format("M/D/YY"),
          record.dateOfDivorce
            ? moment(record.dateOfDivorce).format("M/D/YY")
            : "",
        ])}
        onEdit={onEdit}
        deleteAction={DeleteMarriageInfo}
        getRecords={getRecords}
      />
      <HistoryModal
        category={category}
        mode={Id ? "Update" : "Add"}
        isOpen={isOpen}
        onClose={onClose}
        onAdd={handleSubmit(onAdd)}
        onUpdate={handleSubmit(onUpdate)}
        isSubmitting={isSubmitting}
      >
        <FormRow
          label="Date of Marriage"
          size="sm"
          fieldError={errors.DateOfMarriage}
        >
          <Input
            type="date"
            textTransform="uppercase"
            {...register("DateOfMarriage", { required: REQUIRED_FIELD })}
          />
        </FormRow>
        <FormRow
          label="City of Marriage"
          size="sm"
          fieldError={errors.CityOfMarriage}
        >
          <InputWrapper
            name="CityOfMarriage"
            control={control}
            rules={{ required: REQUIRED_FIELD }}
            placeholder="Brooklyn"
            maskInputField={isTextMasked}
          />
        </FormRow>
        <FormRow
          label="State of Marriage"
          size="sm"
          fieldError={errors.StateOfMarriage}
        >
          <Controller
            name="StateOfMarriage"
            control={control}
            rules={{ required: REQUIRED_FIELD }}
            render={({ field }) => (
              <LocationSelect
                valueKey="name"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </FormRow>

        <FormRow label="Spouse Name" size="sm" fieldError={errors.SpouseName}>
          <InputWrapper
            name="SpouseName"
            control={control}
            rules={{ required: REQUIRED_FIELD }}
            placeholder=""
            maskInputField={isTextMasked}
          />
        </FormRow>
        <FormRow
          label="Spouse's Date of Birth"
          size="sm"
          fieldError={errors.SpouseDob}
        >
          <DateOfBirthInput register={register} fieldName="SpouseDob" />
        </FormRow>
        <FormRow label="Spouse's SSN" size="sm" fieldError={errors.SpouseSSN}>
          <Input
            type="password"
            placeholder="*********"
            {...register("SpouseSSN", {
              required: REQUIRED_FIELD,
              pattern: {
                value: /^\d{9}$/,
                message: "SSN must be exactly 9 digits",
              },
            })}
          />
        </FormRow>

        <FormRow
          label="Date of Divorce"
          size="sm"
          fieldError={errors.DateOfDivorce}
        >
          <Input
            type="date"
            textTransform="uppercase"
            {...register("DateOfDivorce")}
          />
        </FormRow>
        <FormRow
          label="City of Divorce"
          size="sm"
          fieldError={errors.CityOfDivorce}
        >
          <InputWrapper
            name="CityOfDivorce"
            control={control}
            rules={{ required: DateOfDivorce ? REQUIRED_FIELD : false }}
            placeholder="Brooklyn"
            maskInputField={isTextMasked}
          />
        </FormRow>
        <FormRow
          label="State of Divorce"
          size="sm"
          fieldError={errors.StateOfDivorce}
        >
          <Controller
            name="StateOfDivorce"
            control={control}
            rules={{ required: DateOfDivorce ? REQUIRED_FIELD : false }}
            render={({ field }) => (
              <LocationSelect
                valueKey="name"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </FormRow>
      </HistoryModal>
    </HistorySection>
  );
};

export default MarriageHistoryPageContent;
